import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SystemPage, SystemSection } from "./SystemPage.js";
import { fetchRelated, fetchRelatedStatus, type RelatedHit } from "../api.js";
import { repoHref } from "../data/repo.js";

const MD = /\.(md|markdown)$/;

const SAMPLE = `Paste a document here — a page's text, a chat transcript, meeting
notes — and this finds the wiki pages closest to it.

Short queries make poor vectors, so this deliberately takes a whole document
rather than a search box. The equivalent from a terminal is:

  cat notes.md | datawiki related`;

/**
 * `/_vss` — the semantic search demo (docs/wiki/Vss.md).
 *
 * Deliberately a *document* box, not a search box: embeddings are strong when both
 * sides of the comparison are long, and weak on the two-word queries a search box
 * gets. This is the visible surface for the same `nearest()` primitive behind
 * `POST /api/related` and `datawiki related`, so it doubles as the honest quality
 * test — if the hits here look like noise, semantic retrieval isn't earning its keep
 * on this content, and we've learned that cheaply.
 */
export function VssView() {
  const [text, setText] = useState("");
  const [hits, setHits] = useState<RelatedHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{
    total: number;
    done: number;
    ready: boolean;
    model: string;
  } | null>(null);

  // Coverage: embedding runs in the background, so partial coverage is the normal
  // state and the page should say so rather than looking broken.
  useEffect(() => {
    let live = true;
    const poll = (): void => {
      void fetchRelatedStatus()
        .then((s) => live && setStatus(s))
        .catch(() => live && setStatus(null));
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  const run = (): void => {
    const body = text.trim();
    if (body.length < 8) {
      setError("paste at least a sentence — this matches documents, not keywords");
      return;
    }
    setBusy(true);
    setError(null);
    fetchRelated(body, 10)
      .then((r) => setHits(r.hits))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        setHits(null);
      })
      .finally(() => setBusy(false));
  };

  const pct = status && status.total > 0 ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <SystemPage
      title="Semantic search"
      lead={
        <>
          Find pages by <em>meaning</em>, from a document rather than a query. Same
          primitive as <code>POST /api/related</code> and{" "}
          <code>cat doc.md | datawiki related</code>. Back to{" "}
          <Link to="/_tools">tools</Link>.
        </>
      }
    >
      <SystemSection title="Coverage">
        {status ? (
          <p className="dw-vss-status">
            <strong>
              {status.done}/{status.total}
            </strong>{" "}
            prose chunks embedded ({pct}%){" "}
            {status.ready ? (
              <span className="dw-vss-ok">complete</span>
            ) : (
              <span className="dw-muted">— still building in the background</span>
            )}
            <br />
            <span className="dw-muted">model: {status.model}</span>
          </p>
        ) : (
          <p className="dw-muted">
            coverage unavailable — the embedding model may not be baked
            (<code>pnpm bake:model</code>).
          </p>
        )}
      </SystemSection>

      <SystemSection title="Find similar">
        <textarea
          className="dw-vss-input"
          rows={10}
          value={text}
          placeholder={SAMPLE}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
        />
        <div className="dw-vss-actions">
          <button type="button" onClick={run} disabled={busy}>
            {busy ? "searching…" : "find similar"}
          </button>
          <span className="dw-muted">{text.trim().length} chars</span>
        </div>
        {error && (
          <p className="dw-vss-error" role="alert">
            {error}
          </p>
        )}
      </SystemSection>

      {hits && (
        <SystemSection title={`Nearest pages (${hits.length})`}>
          {hits.length === 0 ? (
            <p className="dw-muted">
              nothing close enough. If coverage above is low, embeddings are still
              building.
            </p>
          ) : (
            <ol className="dw-vss-hits">
              {hits.map((h) => (
                <li key={`${h.repo}:${h.path}`}>
                  <Link to={repoHref(h.repo, h.path.replace(MD, ""))}>
                    {h.repo}/{h.path}
                  </Link>
                  <span className="dw-vss-score" title="cosine similarity, 1 = identical">
                    {h.score.toFixed(3)}
                  </span>
                  <span className="dw-vss-snip">{h.text.replace(/\s+/g, " ").slice(0, 220)}</span>
                </li>
              ))}
            </ol>
          )}
        </SystemSection>
      )}
    </SystemPage>
  );
}
